import { supabase } from '../supabase';
import type { SnapInvite, PaginatedResponse } from '@/types/database.types';

export async function getSnapInvites(snapId: string, page = 1, limit = 10): Promise<PaginatedResponse<SnapInvite>> {
  // Get total count first
  const { count } = await supabase
    .from('snap_invites')
    .select('*', { count: 'exact', head: true })
    .eq('snap_id', snapId);

  // Get paginated invites with user information
  const { data: invites, error } = await supabase.rpc('get_snap_invites', {
    p_snap_id: snapId,
    p_status: null,
    p_user_id: null
  });

  if (error) throw error;

  return {
    data: invites || [],
    total: count || 0,
    page,
    limit
  };
}

export async function getPendingInvites(page = 1, limit = 10): Promise<PaginatedResponse<SnapInvite>> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  // Get total count first
  const { count } = await supabase
    .from('snap_invites')
    .select('*', { count: 'exact', head: true })
    .eq('invited_user_id', user.id)
    .eq('status', 'pending');

  // Get paginated invites with snap and inviter information
  const { data: invites, error } = await supabase.rpc('get_snap_invites', {
    p_snap_id: null,
    p_status: 'pending',
    p_user_id: user.id
  });

  if (error) throw error;

  return {
    data: invites || [],
    total: count || 0,
    page,
    limit
  };
}

export async function sendInvites(
  snapId: string,
  userIds: string[],
  message?: string
): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { error } = await supabase.rpc('send_snap_invites', {
    p_snap_id: snapId,
    p_user_ids: userIds,
    p_message: message
  });

  if (error) throw error;
}

export async function respondToInvite(
  inviteId: string,
  accept: boolean
): Promise<void> {
  const { error } = await supabase.rpc('handle_snap_invite', {
    p_invite_id: inviteId,
    p_status: accept ? 'accepted' : 'declined'
  });

  if (error) throw error;
}

export async function cancelInvite(inviteId: string): Promise<void> {
  const { error } = await supabase
    .from('snap_invites')
    .delete()
    .eq('id', inviteId);

  if (error) throw error;
}